import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel, RowSelectedEvent } from 'ag-grid-enterprise';
import {
  PrimaryTrade,
  ProposalRequest,
  ProposalRequestVendor
} from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';

@Component({
  selector: 'app-add-proposal-request-vive-vendors-modal',
  templateUrl: './add-proposal-request-vive-vendors-modal.component.html',
  styleUrls: ['./add-proposal-request-vive-vendors-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddProposalRequestViveVendorsModalComponent implements OnInit {
  constructor(
    private ActiveModal: NgbActiveModal,
    private ToastService: ToastService,
    private TradeService: TradeService
  ) {}

  title: string = 'Add VIVE Vendor to Bid';

  @Input() ProposalRequest!: ProposalRequest;
  @Input() ProposalRequestVendorsMap: Map<number, ProposalRequestVendor> =
    new Map([]);

  FilterModel: FilterModel = {
    viveStatus: {
      filterType: 'set',
      values: ['Approved']
    }
  };

  selectedProposalRequestVendorIds = new Set<string | number>([]);
  clearGridFilters: boolean = false;

  // This replaces the additionalQueryData on the CompanyVendors grid.
  overrideAdditionalQueryData: { [key: string]: any } = {
    attributes: ['id', 'CompanyId', 'VendorId', 'CompanyComplianceTypeId'],
    aliasedAttributes: ['vendorPriorityLevel'],
    Company: {
      attributes: ['id'],
      options: { required: true }
    },
    Vendor: {
      options: { required: true },
      attributes: ['id', 'legalName'],
      BidContacts: {
        options: { required: false, separate: true }
      },
      StreetAddress: {
        options: { required: true },
        attributes: ['address1', 'address2', 'city', 'state', 'zip'],
        SubNationalDivision: {
          options: { required: true }
        }
      }
    }
  };

  // This is used to store the original state of the supplied query data object before
  // it becomes manipulated.
  // originalSuppliedQueryData!: GenericObject;

  suppliedQueryData: GenericObject = {
    order: [
      ['orderBySpotlightVendorsWithApprovedOrRenewingStatus', 'ASC'],
      ['orderByVendorPriorityLevel', 'ASC'],
      ['orderBySpecificCompanyVendorStatuses'],
      ['viveStatus', 'DESC'],
      ['id', 'DESC']
    ]
  };

  // Contains selected trades & trade categories, which are then applied to the grid as a filter.
  SelectedTradesAndCategories: {
    Trades: PrimaryTrade[];
  } = {
    Trades: []
  };

  // May want to restructure this away from using the 'vendorTrades' alias. Sub-query per row can be
  // costly.
  reconfigureSuppliedQueryData() {
    const { Trades } = this.SelectedTradesAndCategories;
    if (Trades.length > 0) {
      this.suppliedQueryData = structuredClone(
        Object.assign(this.suppliedQueryData, {
          vendorTrades: {
            $or: Trades.map((Trade) => ({ $like: `%${Trade.name}%` }))
          }
        })
      );
    } else {
      const suppliedQueryData = structuredClone(this.suppliedQueryData);
      delete suppliedQueryData['vendorTrades'];
      this.suppliedQueryData = suppliedQueryData;
    }
  }

  handleTradeSelection({ Trades }: { Trades: PrimaryTrade[] }) {
    Object.assign(this.SelectedTradesAndCategories, { Trades });
    this.reconfigureSuppliedQueryData();
  }

  vendorSelection(event: RowSelectedEvent) {
    const { data, node } = event;

    const { Vendor } = data;
    const { id: ProposalRequestId } = this.ProposalRequest;
    const { id: VendorId, dba, legalName, BidContacts, StreetAddress } = Vendor;

    if (node.isSelected()) {
      if (!BidContacts) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error loading the Bid Contact.',
          { returnError: true }
        );
      }

      if (!StreetAddress) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error loading the Street Address.',
          { returnError: true }
        );
      }

      const allBidContactEmails = BidContacts.filter(
        (contact: any) => contact.email
      ).map((contact: any) => contact.email);

      if (allBidContactEmails.length === 0) {
        throw this.ToastService.addErrorToast(
          'Error',
          'No bid contact emails found for this vendor.',
          { returnError: true }
        );
      }

      const primaryBidContact =
        BidContacts.find((contact: any) => contact.isPrimaryBid) ||
        BidContacts[0];

      const otherBidContactEmails = BidContacts.filter(
        (contact: any) =>
          contact.email && contact.email !== primaryBidContact.email
      ).map((contact: any) => contact.email);

      const emailsString = [
        primaryBidContact.email,
        ...otherBidContactEmails
      ].join(', ');

      const {
        firstName,
        lastName,
        phone1: phone,
        phone2,
        email
      } = primaryBidContact;
      const { address1, city, state, zip, SubNationalDivision } = StreetAddress;

      if (!SubNationalDivision) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error loading State/Province/Territory information.',
          { returnError: true }
        );
      }

      const { id: SubNationalDivisionId } = SubNationalDivision;

      if (!this.ProposalRequestVendorsMap.has(VendorId)) {
        this.ProposalRequestVendorsMap.set(
          VendorId,
          new ProposalRequestVendor({
            VendorId,
            ProposalRequestId,
            dba,
            legalName,
            firstName,
            lastName,
            phone,
            phone2,
            email: emailsString,
            SubNationalDivisionId,
            Address: {
              address1,
              city,
              state,
              zip
            }
          })
        );
      }
    } else {
      this.ProposalRequestVendorsMap.delete(VendorId);
    }
  }

  continue() {
    this.ActiveModal.close(this.ProposalRequestVendorsMap);
  }

  cancel() {
    this.ActiveModal.dismiss();
  }

  ngOnInit(): void {
    if (!this.ProposalRequest) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request.',
        { returnError: true }
      );
    }

    this.ProposalRequestVendorsMap.forEach((prv, index) => {
      this.selectedProposalRequestVendorIds.add(index);
    });
  }
}
